import type { ResizableColumnDef } from '@protoss/ui';
import {
  Button,
  FileTextIcon,
  IconSize,
  PersonIcon,
  ResizableTable,
  SkeletonBlock,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { useCustomers } from '../hooks/useCustomers';
import type { CustomerFormPayload, CustomerRecord } from '../services/customersApi';
import { customersApi } from '../services/customersApi';
import { CustomerAvatar } from './CustomerAvatar';
import { CustomerForm, type CustomerImageSubmission } from './CustomerForm';
import type { NewAttributeDraft } from './CustomerRightRailPanels';

const PAGE_SIZE = 25;

interface CustomerListPageProps {
  projectUuid?: string;
  canEdit: boolean;
  creating: boolean;
  refreshKey?: number;
  onCreateStart: () => void;
  onCreateCancel: () => void;
  onSelectCustomer: (customerUuid: string) => void;
  onCustomerCountChange?: (count: number, loading: boolean) => void;
  onSelectionChange?: (selected: { uuid: string; name: string; email: string | null }[]) => void;
  onImportCsv?: () => void;
  onImportArchon?: () => void;
  formId?: string;
  hideActions?: boolean;
  onFormStateChange?: (state: { submitting: boolean; canSubmit: boolean }) => void;
  searchQuery?: string;
  databaseSearchTrigger?: number;
  onSearchUiStateChange?: (state: {
    showDatabaseAction: boolean;
    searchingDatabase: boolean;
  }) => void;
  pendingAttributeDraft?: NewAttributeDraft | null;
  onPendingAttributeConsumed?: () => void;
  deletedAttributeSignal?: { fieldKey: string; name: string; nonce: number } | null;
}

function CopyableEmailCell({ email }: { email: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <button
      type="button"
      className="customer-list__col-email"
      onClick={(e) => {
        e.stopPropagation();
        void copy(email);
      }}
      aria-label={`Copy email ${email}`}
    >
      <span className="customer-list__col-email-text">{email}</span>
      <span className="customer-list__col-email-tooltip" aria-hidden="true">
        {copied ? 'Copied' : 'Click to copy'}
      </span>
    </button>
  );
}

/**
 * CustomerListPage - master list of customer records with inline create/edit entry.
 */
export function CustomerListPage({
  projectUuid,
  canEdit,
  creating,
  refreshKey = 0,
  onCreateStart,
  onCreateCancel,
  onSelectCustomer,
  onCustomerCountChange,
  onSelectionChange,
  formId,
  hideActions,
  onFormStateChange,
  searchQuery = '',
  databaseSearchTrigger = 0,
  onSearchUiStateChange,
  pendingAttributeDraft,
  onPendingAttributeConsumed,
  deletedAttributeSignal,
  onImportCsv,
  onImportArchon,
}: CustomerListPageProps) {
  const { customers, loading, error, reload, deleteCustomer } = useCustomers(projectUuid);
  const [confirming, setConfirming] = React.useState<string | null>(null);
  const [selectedUuids, setSelectedUuids] = React.useState<Set<string>>(new Set());
  const [page, setPage] = React.useState(1);
  const [databaseSearchResults, setDatabaseSearchResults] = React.useState<CustomerRecord[] | null>(
    null
  );
  const [databaseSearchQuery, setDatabaseSearchQuery] = React.useState<string | null>(null);
  const [databaseSearching, setDatabaseSearching] = React.useState(false);
  const lastHandledDatabaseSearchTrigger = React.useRef(0);

  // Reset to first page whenever the customer list changes
  React.useEffect(() => {
    setPage(1);
  }, [customers]);

  React.useEffect(() => {
    if (!confirming) return;
    const clear = () => setConfirming(null);
    window.addEventListener('click', clear);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clear();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', clear);
      window.removeEventListener('keydown', onKey);
    };
  }, [confirming]);

  React.useEffect(() => {
    if (refreshKey > 0) {
      reload();
      setSelectedUuids(new Set());
    }
  }, [refreshKey, reload]);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  React.useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      setDatabaseSearchResults(null);
      setDatabaseSearchQuery(null);
      setDatabaseSearching(false);
      return;
    }

    if (databaseSearchQuery !== normalizedQuery) {
      setDatabaseSearchResults(null);
      setDatabaseSearchQuery(null);
      setDatabaseSearching(false);
    }
  }, [databaseSearchQuery, searchQuery]);

  React.useEffect(() => {
    const normalizedQuery = searchQuery.trim();
    if (!normalizedQuery || databaseSearchTrigger <= 0) return;
    if (lastHandledDatabaseSearchTrigger.current === databaseSearchTrigger) return;

    lastHandledDatabaseSearchTrigger.current = databaseSearchTrigger;

    let cancelled = false;
    setDatabaseSearching(true);

    void (async () => {
      try {
        const { customers: results } = await customersApi.listCustomers(projectUuid, {
          query: normalizedQuery,
        });
        if (cancelled) return;
        setDatabaseSearchResults(results);
        setDatabaseSearchQuery(normalizedQuery.toLowerCase());
        setPage(1);
      } catch {
        if (cancelled) return;
        setDatabaseSearchResults([]);
        setDatabaseSearchQuery(normalizedQuery.toLowerCase());
      } finally {
        if (!cancelled) {
          setDatabaseSearching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [databaseSearchTrigger, projectUuid, searchQuery]);

  React.useEffect(() => {
    onCustomerCountChange?.(customers.length, loading);
  }, [customers.length, loading, onCustomerCountChange]);

  React.useEffect(() => {
    if (!onSelectionChange) return;
    const selected = [...selectedUuids]
      .map((uuid) => customers.find((c) => c.uuid === uuid))
      .filter((c): c is NonNullable<typeof c> => c !== undefined)
      .map((c) => ({ uuid: c.uuid, name: c.name, email: c.email }));
    onSelectionChange(selected);
  }, [selectedUuids, customers, onSelectionChange]);

  const [imageUploadError, setImageUploadError] = React.useState<string | null>(null);

  const handleCreate = React.useCallback(
    async (data: CustomerFormPayload, image: CustomerImageSubmission) => {
      setImageUploadError(null);
      const { customer } = await customersApi.createCustomer(projectUuid, data);
      // Navigate directly to detail view; the detail page will show its skeleton while loading.
      onSelectCustomer(customer.uuid);

      if (image.imageFile) {
        try {
          await customersApi.uploadCustomerImage(projectUuid, customer.uuid, image.imageFile);
        } catch {
          // User is already on detail view; avoid bouncing back just to show this warning.
          // They can retry image upload from the customer detail page.
        }
      }

      // Keep list cache fresh for when user navigates back to customers list.
      void reload();
    },
    [projectUuid, reload, onSelectCustomer]
  );

  const handleDelete = React.useCallback(
    async (customerUuid: string, event: React.MouseEvent) => {
      event.stopPropagation();
      if (confirming === customerUuid) {
        setConfirming(null);
        try {
          await deleteCustomer(customerUuid);
        } catch {
          reload();
        }
      } else {
        setConfirming(customerUuid);
      }
    },
    [confirming, deleteCustomer, reload]
  );

  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  const handleSort = React.useCallback(
    (key: string) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDir('asc');
      } else if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        // desc → clear (back to server order)
        setSortKey(null);
        setSortDir('asc');
      }
    },
    [sortKey, sortDir]
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const locallyFilteredCustomers = React.useMemo(() => {
    return normalizedSearchQuery
      ? customers.filter((customer) => {
          const haystack = [customer.name, customer.email, customer.companyName]
            .filter((value): value is string => Boolean(value))
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalizedSearchQuery);
        })
      : customers;
  }, [customers, normalizedSearchQuery]);

  const usingDatabaseSearchResults =
    databaseSearchQuery === normalizedSearchQuery && databaseSearchResults !== null;

  const sourceCustomers = usingDatabaseSearchResults
    ? databaseSearchResults
    : locallyFilteredCustomers;

  React.useEffect(() => {
    onSearchUiStateChange?.({
      showDatabaseAction: Boolean(normalizedSearchQuery),
      searchingDatabase: databaseSearching,
    });
  }, [databaseSearching, normalizedSearchQuery, onSearchUiStateChange]);

  const sortedCustomers = React.useMemo(() => {
    const filteredCustomers = sourceCustomers;

    if (!sortKey) return filteredCustomers;
    return [...filteredCustomers].sort((a, b) => {
      const aVal = String((a as unknown as Record<string, unknown>)[sortKey] ?? '');
      const bVal = String((b as unknown as Record<string, unknown>)[sortKey] ?? '');
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [sortDir, sortKey, sourceCustomers]);

  const paginatedCustomers = React.useMemo(
    () => sortedCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedCustomers, page]
  );

  const handleSelectAll = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelectedUuids(new Set(paginatedCustomers.map((c) => c.uuid)));
      } else {
        setSelectedUuids(new Set());
      }
    },
    [paginatedCustomers]
  );

  const handleSelectRow = React.useCallback((uuid: string, checked: boolean) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uuid);
      else next.delete(uuid);
      return next;
    });
  }, []);

  const columns = React.useMemo<ResizableColumnDef<(typeof customers)[0]>[]>(() => {
    const allSelected =
      paginatedCustomers.length > 0 && paginatedCustomers.every((c) => selectedUuids.has(c.uuid));
    const someSelected = !allSelected && paginatedCustomers.some((c) => selectedUuids.has(c.uuid));

    const cols: ResizableColumnDef<(typeof customers)[0]>[] = [
      {
        key: 'checkbox',
        label: (
          <input
            type="checkbox"
            className="rt-table__checkbox"
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            checked={allSelected}
            onChange={handleSelectAll}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select all customers"
          />
        ),
        defaultWidth: 40,
        minWidth: 40,
        maxWidth: 40,
        headerClassName: 'rt-table__col--checkbox',
        cellClassName: 'rt-table__col--checkbox',
        stopRowClick: true,
        skeletonContent: (
          <div className="rt-table__skeleton-cell rt-table__skeleton-cell--square">
            <SkeletonBlock />
          </div>
        ),
        render: (c) => (
          <input
            type="checkbox"
            className="rt-table__checkbox"
            checked={selectedUuids.has(c.uuid)}
            onChange={(e) => {
              e.stopPropagation();
              handleSelectRow(c.uuid, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${c.name}`}
          />
        ),
      },
      {
        key: 'name',
        label: 'Name',
        defaultWidth: 220,
        minWidth: 100,
        sortable: true,
        render: (c) => (
          <div className="customer-list__name-cell">
            <CustomerAvatar name={c.name} asset={c.primaryImageAsset} size="sm" shape="square" />
            <span className="customer-list__col-name">{c.name}</span>
          </div>
        ),
      },
      {
        key: 'email',
        label: 'Email',
        defaultWidth: 200,
        minWidth: 100,
        sortable: true,
        cellClassName: 'customer-list__email-cell',
        render: (c) =>
          c.email ? (
            <CopyableEmailCell email={c.email} />
          ) : (
            <span className="customer-list__col-text">-</span>
          ),
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        defaultWidth: 180,
        minWidth: 80,
        render: (c) =>
          c.linkedin ? (
            <a
              href={c.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="customer-list__col-link"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {c.linkedin}
            </a>
          ) : (
            <span className="customer-list__col-text">-</span>
          ),
      },
      {
        key: 'title',
        label: 'Title',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (c) => <span className="customer-list__col-text">{c.title ?? '-'}</span>,
      },
      {
        key: 'companyName',
        label: 'Company',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (c) => <span className="customer-list__col-text">{c.companyName ?? '-'}</span>,
      },
      {
        key: 'industry',
        label: 'Industry',
        defaultWidth: 140,
        minWidth: 80,
        sortable: true,
        render: (c) => <span className="customer-list__col-text">{c.industry ?? '-'}</span>,
      },
      {
        key: 'location',
        label: 'Location',
        defaultWidth: 140,
        minWidth: 80,
        sortable: true,
        render: (c) => <span className="customer-list__col-text">{c.location ?? '-'}</span>,
      },
    ];

    return cols;
  }, [
    confirming,
    handleDelete,
    handleSelectAll,
    handleSelectRow,
    selectedUuids,
    paginatedCustomers,
  ]);

  const emptyState = !creating ? (
    <div className="customer-list__empty">
      <PersonIcon
        width={IconSize.xl}
        height={IconSize.xl}
        className="customer-list__empty-icon"
        aria-hidden="true"
      />
      <h3 className="customer-list__empty-title">
        {searchQuery.trim()
          ? usingDatabaseSearchResults
            ? 'No record in the database'
            : 'No matching customers'
          : 'No customers yet'}
      </h3>
      {searchQuery.trim() && (
        <p className="customer-list__empty-body">
          {usingDatabaseSearchResults
            ? 'No customer records matched your database search.'
            : 'Try a different search term for name, email, or company.'}
        </p>
      )}
      {canEdit && !searchQuery.trim() && (
        <div className="customer-list__empty-actions">
          <Button variant="primary" size="sm" onClick={onCreateStart}>
            New Customer
          </Button>
          {onImportCsv && (
            <Button variant="outline" size="sm" onClick={onImportCsv}>
              <FileTextIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
              Import by CSV
            </Button>
          )}
          {onImportArchon && (
            <Button variant="outline" size="sm" onClick={onImportArchon}>
              From Document
            </Button>
          )}
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <div className="customer-list">
      {creating && (
        <div className="customer-list__create-panel">
          <CustomerForm
            projectUuid={projectUuid}
            onSubmit={handleCreate}
            onCancel={onCreateCancel}
            submitLabel="Create"
            formId={formId}
            hideActions={hideActions}
            onFormStateChange={onFormStateChange}
            pendingAttributeDraft={pendingAttributeDraft}
            onPendingAttributeConsumed={onPendingAttributeConsumed}
            deletedAttributeSignal={deletedAttributeSignal}
          />
        </div>
      )}

      {imageUploadError && (
        <div className="customer-list__warning" role="alert">
          <p>{imageUploadError}</p>
          <Button variant="outline" size="sm" onClick={() => setImageUploadError(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {error && !creating && <PageErrorState message={error} onRetry={reload} />}

      {!error && !creating && (
        <ResizableTable
          columns={columns}
          rows={paginatedCustomers}
          getRowKey={(c) => c.uuid}
          hasSelectionColumn
          loading={loading}
          emptyState={emptyState}
          onRowClick={(c) => onSelectCustomer(c.uuid)}
          aria-label="Customers"
          sortKey={sortKey ?? undefined}
          sortDir={sortDir}
          onSort={handleSort}
          rowActions={
            canEdit
              ? (c) => (
                  <button
                    className={`customer-list__delete-btn${confirming === c.uuid ? ' customer-list__delete-btn--confirming' : ''}`}
                    onClick={(event) => handleDelete(c.uuid, event)}
                    type="button"
                    aria-label={
                      confirming === c.uuid ? `Confirm delete "${c.name}"` : `Delete "${c.name}"`
                    }
                  >
                    {confirming === c.uuid ? 'Confirm?' : '×'}
                  </button>
                )
              : undefined
          }
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            total: sortedCustomers.length,
            onPageChange: setPage,
          }}
        />
      )}
    </div>
  );
}
